import { richText } from '../../util/richText';
import css from './ListingPage.module.css';
import React from 'react';
import { camelCase, startCase } from 'lodash/string';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const sanitiseText = (fieldName, text, fallbackDisplayName) => {
  const config = {
    instagram: () => extractInstagramUsername(text?.trim()),
    facebook: () =>
      extractFacebookUsername({
        input: text?.trim(),
        fallbackFacebookDisplayName: fallbackDisplayName,
      }),
    websiteUrl: () => (
      <a href={text} target="_blank" rel="noopener noreferrer">
        {text?.trim()?.replace(/^(?:https?:\/\/)?([^?#]+(?:\.[a-z]{2,6})?)(?:[/?#].*)?$/, '$1')}
      </a>
    ),
    whatsappNumber: () => parsePhoneNumber(text),
  };

  const defaultConfig = () =>
    richText(text, {
      linkify: true,
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
      breakChars: '/',
    });

  return (config[fieldName] || defaultConfig)();
};

export const extractInstagramUsername = input => {
  const urlPattern = /(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^/?#&]+)/i;
  const usernamePattern = /^[a-zA-Z0-9._]+$/;
  let username;

  const urlMatch = input.match(urlPattern);
  if (urlMatch) {
    username = urlMatch[1];
  }

  if (usernamePattern.test(input)) {
    username = input;
  }

  return (
    (username && (
      <a href={`https://www.instagram.com/${username}`} target="_blank" rel="noopener noreferrer">
        @{username}
      </a>
    )) ||
    input
  );
};

export const extractFacebookUsername = ({ input, fallbackFacebookDisplayName = 'facebook' }) => {
  if (!input) return input;

  let path; // URL path to link to
  let label; // Display label

  const isNumericId = value => /^\d+$/.test(value);

  try {
    const url = new URL(input.startsWith('http') ? input : `https://${input}`);

    if (url.hostname.includes('facebook.com')) {
      const parts = url.pathname.split('/').filter(Boolean);

      // /groups/<group-name>
      if (parts[0] === 'groups' && parts[1]) {
        path = `groups/${parts[1]}`;
        label = parts[1];
      }

      // /p/<id-or-name>
      else if (parts[0] === 'p' && parts[1]) {
        path = parts[1];
        label = isNumericId(parts[1])
          ? startCase(camelCase(fallbackFacebookDisplayName)).replace(/ /g, '')
          : parts[1];
      }

      // /profile.php?id=<id>
      else if (parts[0] === 'profile.php') {
        const id = url.searchParams.get('id');
        if (id) {
          path = `profile.php?id=${id}`;
          label = startCase(camelCase(fallbackFacebookDisplayName)).replace(/ /g, '');
        }
      }

      // /<username>
      else if (parts[0]) {
        path = parts[0];
        label = parts[0];
      }
    }
  } catch {
    // Raw username fallback
    const usernamePattern = /^[a-zA-Z0-9.-]+$/;
    if (usernamePattern.test(input)) {
      path = input;
      label = input;
    }
  }

  return (
    (path && (
      <a href={`https://www.facebook.com/${path}`} target="_blank" rel="noopener noreferrer">
        @{label}
      </a>
    )) ||
    input
  );
};

export const parsePhoneNumber = input => {
  if (input == null) return ``;

  // Remove all non-digit characters
  let digits = input.toString().replace(/\D/g, '');

  // Remove UK country code if present (44 or 0044)
  if (digits.startsWith('0044')) {
    digits = digits.slice(4);
  } else if (digits.startsWith('44')) {
    digits = digits.slice(2);
  }

  // Ensure leading 0
  if (!digits.startsWith('0')) {
    digits = '0' + digits;
  }

  // Match UK mobile / standard format
  const match = digits.match(/^0(\d{4})(\d{6})$/);

  if (!match) return null;

  const [, part1, part2] = match;

  return `+44 ${part1} ${part2}`;
};

export const parseBoolean = field => field === 'true' || field === true;
